import { DialogCloseButtonVariant } from '@/components/dialog-close-button';
import { useIsDarkMode, useProvidedTheme } from '@/hooks/use-provided-theme';
import {
    SimpleDialogBackground,
    SimpleDialogImageProps,
} from '@/libs/material/simple-dialog/types';
import { sxAppend } from '@/libs/material/sx';
import { DS_HEIGHT, DS_BOX_SHADOW } from '@calm/evexia-design-tokens';
import {
    Theme,
    ThemeProvider,
    Typography,
    Box,
    SxProps,
} from '@rhg/libs/material';
import SimpleDialog from '@rhg/libs/material/simple-dialog';
import { ColorMode } from '@rhg/libs/material/theme';
import { PropsWithChildren, useEffect, useId } from 'react';
import { SheetDialogProps, SheetDialogVariant } from './types';

const SHEET_DIALOG_MAX_WIDTH_PX = 676;
const styleIconContainer = {
    height: DS_HEIGHT.AVATAR_MEDIUM,
    width: DS_HEIGHT.AVATAR_MEDIUM,
    '& > svg': {
        // Need to override default styles in this component
        height: { xs: '24px' },
        width: { xs: '24px' },
    },
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: (theme: Theme) =>
        theme.palette.mode === 'light' ? 'warning.light' : 'warning.dark',
};

const styleIconContainerPrimary = {
    backgroundColor: (theme: Theme) =>
        theme.palette.mode === 'light' ? 'primary.light' : 'mask.black.20p',
    color: (theme: Theme) =>
        theme.palette.mode === 'light' ? 'primary.main' : 'primary.dark',
};
const styleIconContainerSecondary = {
    backgroundColor: (theme: Theme) =>
        theme.palette.mode === 'light' ? 'secondary.light' : 'mask.black.20p',
    color: (theme: Theme) =>
        theme.palette.mode === 'light' ? 'secondary.main' : 'secondary.dark',
};
const styleIconContainerWarning = {
    backgroundColor: (theme: Theme) =>
        theme.palette.mode === 'light' ? 'warning.light' : 'warning.dark',
    color: 'warning.main',
};
const styleIconContainerInfo = {
    backgroundColor: (theme: Theme) =>
        theme.palette.mode === 'light' ? 'info.light' : 'info.dark',
    color: 'info.main',
};

const styleTitleContainer = { mb: 2 };
const styleTitle = { mb: 0 };

const styleButton = {
    width: 'initial',
    minWidth: '140px',
    height: '36px',
    m: 0,
};

const styleStackedButton = {
    ...styleButton,
    width: '100%',
    maxWidth: '268px',
};
const styleCloseButtonContainer = { p: 1 };

// Fullscreen actions otherwise get their own surface and a light-mode top line; a sheet is one surface.
const styleBottomActionsWrapper = {
    p: (theme: Theme) => theme.ds.spacingUnits.CONTENT,
    backgroundColor: 'transparent',
    borderTop: 'none',
};

const styleStackedButtons = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
};

const styleContentWrapper = {
    pb: 0,
};
const styleFullWidthContentWrapper = {
    ...styleContentWrapper,
    maxWidth: 'none',
};
const styleFullWidthContentContainer = {
    px: (theme: Theme) => theme.ds.spacingUnits.CONTENT,
};

function ForcedThemeProvider({
    mode,
    children,
}: PropsWithChildren<{ mode: ColorMode }>) {
    const theme = useProvidedTheme(mode);

    return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
}

function ConditionalThemeProvider({
    isForcedTheme,
    mode,
    children,
}: PropsWithChildren<{ isForcedTheme: boolean; mode: ColorMode }>) {
    return isForcedTheme ? (
        <ForcedThemeProvider mode={mode}>{children}</ForcedThemeProvider>
    ) : (
        <>{children}</>
    );
}

const styleFeature = { display: 'flex', boxShadow: DS_BOX_SHADOW.POD };

export function SheetDialogFeature({
    sx,
    children,
}: PropsWithChildren<{ sx?: SxProps<Theme> }>) {
    return <Box sx={sxAppend(styleFeature, sx)}>{children}</Box>;
}

export function SheetDialog({
    onOpen,
    onClose,
    onDismiss,
    sx,
    trackCloseButton,
    trackDialog,
    trackScreen,
    trackFeature,
    trackProps,
    title,
    content,
    contentContainerStyle,
    buttons,
    icon,
    iconColor,
    feature,
    stackBottomActions,
    hideCloseButton,
    variant = SheetDialogVariant.Default,
    imageSrc,
    imageAlt,
    imageSize,
    disableMobileFullscreen = true,
    boundContentHeight,
    fullWidthContent,
    skipDialogViewedTracking,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
}: SheetDialogProps & SimpleDialogImageProps) {
    const id = useId();
    const isDark = useIsDarkMode();

    useEffect(() => {
        onOpen?.();
    }, [onOpen]);

    return (
        <ConditionalThemeProvider
            isForcedTheme={
                variant === SheetDialogVariant.Contrast ||
                variant === SheetDialogVariant.ContrastGradient
            }
            mode={isDark ? 'light' : 'dark'}
        >
            <SimpleDialog
                background={
                    variant === SheetDialogVariant.Gradient ||
                    variant === SheetDialogVariant.ContrastGradient
                        ? SimpleDialogBackground.Full
                        : SimpleDialogBackground.None
                }
                // When `title` is provided, its `srOnly` heading (id={id}) is the
                // canonical accessible name — a caller-supplied `aria-label`/`aria-labelledby`
                // is intentionally ignored rather than merged, since no current
                // consumer passes both.
                aria-label={title ? undefined : ariaLabel}
                aria-labelledby={title ? id : ariaLabelledBy}
                disableMobileFullscreen={disableMobileFullscreen}
                boundContentHeight={boundContentHeight}
                skipDialogViewedTracking={skipDialogViewedTracking}
                imageSrc={imageSrc}
                imageAlt={imageAlt}
                imageSize={imageSize}
                content={
                    <>
                        {title && (
                            <Typography variant="srOnly" id={id}>
                                {title}
                            </Typography>
                        )}

                        {content}
                    </>
                }
                contentWrapperStyle={
                    fullWidthContent
                        ? styleFullWidthContentWrapper
                        : styleContentWrapper
                }
                contentContainerStyle={sxAppend(
                    fullWidthContent && styleFullWidthContentContainer,
                    contentContainerStyle
                )}
                trackCloseButton={trackCloseButton}
                trackDialog={trackDialog}
                trackScreen={trackScreen}
                trackFeature={trackFeature}
                trackProps={trackProps}
                title={title}
                titleVariant="h6"
                iconContainerOverride={feature}
                iconContainerStyle={sxAppend(
                    // This is needed to ensure that iconColor overrides the default styleIconContainer colors
                    styleIconContainer,
                    iconColor === 'primary' && styleIconContainerPrimary,
                    iconColor === 'secondary' && styleIconContainerSecondary,
                    iconColor === 'warning' && styleIconContainerWarning,
                    iconColor === 'info' && styleIconContainerInfo
                )}
                icon={!imageSrc ? icon : undefined}
                fullWidth
                PaperProps={{
                    sx: {
                        maxWidth: SHEET_DIALOG_MAX_WIDTH_PX,
                    },
                }}
                onClose={onClose}
                onDismiss={onDismiss}
                titleStyle={styleTitle}
                titleWrapperStyle={styleTitleContainer}
                isOpen
                transitionDuration={0}
                bottomActionsWrapperStyle={styleBottomActionsWrapper}
                buttonsContainerStyle={
                    stackBottomActions ? styleStackedButtons : undefined
                }
                hideCloseButton={hideCloseButton}
                closeButtonVariant={DialogCloseButtonVariant.Flat}
                closeButtonWrapperStyle={styleCloseButtonContainer}
                buttons={buttons?.map((button) => ({
                    ...button,
                    sx: stackBottomActions ? styleStackedButton : styleButton,
                }))}
                sx={sx}
            />
        </ConditionalThemeProvider>
    );
}
